import React, { CSSProperties, RefObject } from 'react';
import styled, { css } from 'styled-components';
import { SystemIcon } from '../Icon/SystemIcon';
import { Keys } from '../../utils';
import { Skeleton } from '../Skeleton/Skeleton';
import { mergeClassNames } from '../../utils/cssClassNameHelper';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { Typography } from '../Typography/Typography';

export interface ChipProps {
  className?: string;
  label: string | JSX.Element;
  dataTestLabel?: string;
  isLoading?: boolean;
  style?: CSSProperties;
  onDelete?: (e: React.MouseEvent | React.KeyboardEvent) => any;
  onClick?: (e: React.MouseEvent) => any;
  onKeyDown?: (e: React.KeyboardEvent) => any;
  'data-test'?: string;
  deleteDataTest?: string;
  size?: 'small' | 'normal';
  maxWidth?: string;
  variant?: 'default' | 'input';
  tabIndex?: number;
  labelRef?: RefObject<HTMLDivElement>;
}

export const Chip: React.FC<React.PropsWithChildren<ChipProps>> = (props: ChipProps) => {
  const handleEnterKey = (e: React.KeyboardEvent) => {
    if (Keys.is(e, 'enter')) {
      props.onDelete?.(e);
    }
  };
  const size = props.size ?? 'normal';
  if (props.isLoading) {
    return (
      <Skeleton
        height={38}
        variant="rect"
        dataTest={props['data-test']}
        data-test-loading="true"
        className={props.className}
        style={{
          ...props.style,
          minWidth: 150
        }}
      />
    );
  }

  const shouldAddTruncation = !!props.maxWidth;

  return (
    <StyledChip
      data-test={props['data-test'] ?? 'uds-chip'}
      data-test-label={props.dataTestLabel}
      className={mergeClassNames(props.className, { 'uds-chip': true })}
      style={props.style}
      onClick={props.onClick}
      size={size}
      tabIndex={props.tabIndex}
      $noDelete={!props.onDelete}
      onKeyDown={props.onKeyDown}
      $maxWidth={props.maxWidth}
      $inputVariant={props.variant === 'input'}
    >
      {shouldAddTruncation ? (
        <Typography ref={props.labelRef} color={tokens.colorOnSurface} noWrap className="text" tag="div">
          {props.label}
        </Typography>
      ) : (
        <div className="text">{props.label}</div>
      )}

      {props.onDelete ? (
        <StyledCloseChipIcon
          className="x-icon"
          name="close_small"
          dataTest={props.deleteDataTest || 'uds-chip-icon-delete'}
          onClick={props.onDelete}
          onKeyDown={handleEnterKey}
          tabIndex={props.tabIndex ?? 0}
          $inputVariant={props.variant === 'input'}
          size={size === 'small' ? 20 : undefined}
        />
      ) : undefined}
    </StyledChip>
  );
};

const StyledChip = styled.div<{
  size: ChipProps['size'];
  $inputVariant?: boolean;
  $noDelete?: boolean;
  $maxWidth?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colorSurfaceVariant};
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
  padding: ${({ size, $noDelete }) => (size === 'small' ? `2px ${$noDelete ? 8 : 0}px 2px 8px` : '0.5rem')};
  border-radius: 4px;
  height: ${({ size }) => (size === 'small' ? '20px' : undefined)};
  &:focus {
    background: rgba(0, 0, 0, 0.04);
  }
  ${({ $inputVariant }) =>
    $inputVariant
      ? css`
          margin-right: ${({ theme }) => theme.spacingSize.s};
          margin-bottom: ${({ theme }) => theme.spacingSize.s};
          white-space: nowrap;
          display: inline-flex;
          max-width: 100%;
        `
      : undefined}

  .text {
    font-size: ${({ theme, size }) => (size === 'small' ? '13px' : theme.font.sizeSmall)};
    overflow: hidden;
    text-overflow: ellipsis;
  }
  max-width: ${({ $maxWidth }) => $maxWidth};
`;

export const StyledCloseChipIcon = styled(SystemIcon)<{ $inputVariant?: boolean }>`
  cursor: pointer;
  margin-left: ${({ theme }) => theme.spacingSize.xs};
  border-radius: ${({ $inputVariant }) => ($inputVariant ? '0 4px 4px 0' : '50%')};
  transition: 0.2s background-color;

  path.wd-icon-fill {
    fill: ${({ theme }) => theme.colorOnSurface};
  }

  &:focus-within {
    background: rgba(0, 0, 0, 0.15);
  }
  &:hover,
  &:focus-within {
    border-radius: ${({ $inputVariant }) => ($inputVariant ? '0 4px 4px 0' : undefined)};
    background: ${({ theme, $inputVariant }) => ($inputVariant ? theme.colorAlertContainer : undefined)};
    path.wd-icon-fill {
      fill: ${({ theme, $inputVariant }) => ($inputVariant ? theme.colorAlert : undefined)};
    }
  }
`;

export default Chip;
